import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, TextField, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';

export default function AuditTrailsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchJson('/api/audit-trails');
      const trails = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
      setRows(trails);
    } catch (e) {
      setError(e?.message || 'Failed to load audit trails');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? rows.filter(r => JSON.stringify(r).toLowerCase().includes(search.toLowerCase()))
    : rows;

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'action', headerName: 'Action', flex: 1 },
    { field: 'userId', headerName: 'User ID', width: 100 },
    { field: 'details', headerName: 'Details', flex: 2,
      valueGetter: (params) => {
        const d = params?.row?.details;
        return d ? (typeof d === 'string' ? d : JSON.stringify(d)) : '';
      }
    },
    { field: 'createdAt', headerName: 'Date', width: 180,
      valueGetter: (params) => params?.row?.createdAt ? new Date(params.row.createdAt).toLocaleString() : ''
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={2}>
        <Typography variant="h5">{t('auditTrails.title', 'Audit Trails')}</Typography>
        <TextField size="small" label="Search" value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 250 }} />
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <div style={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={loading}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          getRowId={(row) => row.id || Math.random()}
        />
      </div>
    </Box>
  );
}
